import { Button } from '@/components/ui/Button'
import { useEditPoll } from './useEditPoll'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { PollForm } from './PollForm'
import { endTimeRefinement, PollFormSchema, pollFormSchema, startTimeRefinement } from './schema'
import { logger } from '@/observability/logger'
import type { Survey } from '@/data-access/surveys/types'
import { formatEther, Hex } from 'viem'
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	type ResponsiveDialogProps
} from '@/components/ui/ResponsiveDialog'
import { usePepperSupply } from '@/hooks/pepper-contract/usePepperSupply.ts'
import { getMaxSupply } from '@/config/get-max-supply.ts'
import { testIdFactory } from '@/utils/test-id-factory.ts'

export function EditPollDialog({
	onSubmit,
	poll,
	...dialogProps
}: Omit<ResponsiveDialogProps, 'children'> & { onSubmit: (surveyAddress: Hex) => void; poll: Survey }) {
	const { maxSupply } = usePepperSupply()
	const maxVotingCap = maxSupply.status === 'success' ? parseInt(formatEther(maxSupply.data)) : getMaxSupply()

	const pollValues = useMemo(
		() => ({
			question: poll.question,
			answers: poll.answers.map(answer => ({ value: answer.id })),
			votingCap: parseInt(formatEther(BigInt(poll.votingCap))),
			startTime: poll.startDate,
			endTime: poll.endDate
		}),
		[poll]
	)

	const schema = useMemo(
		() =>
			poll.status === 'ongoing'
				? pollFormSchema(maxVotingCap).superRefine(endTimeRefinement)
				: pollFormSchema(maxVotingCap).superRefine((data, ctx) => {
						startTimeRefinement(data, ctx)
						endTimeRefinement(data, ctx)
					}),
		[poll.status]
	)

	const form = useForm<PollFormSchema>({
		resolver: zodResolver(schema),
		defaultValues: pollValues
	})

	const { editPoll } = useEditPoll()

	const onClickSubmit = form.handleSubmit(async values => {
		const { startTime, endTime, ...rest } = values

		try {
			await editPoll({
				currentData: {
					...pollValues,
					answers: pollValues.answers.map(answer => answer.value),
					votingCap: pollValues.votingCap,
					startTime: new Date(pollValues.startTime).getTime(),
					endTime: new Date(pollValues.endTime).getTime()
				},
				newData: {
					...rest,
					answers: values.answers.map(answer => answer.value),
					votingCap: values.votingCap,
					startTime: new Date(startTime).getTime(),
					endTime: new Date(endTime).getTime()
				},
				address: poll.surveyContract
			})

			toast.success('Poll edited successfully.')
			onSubmit?.(poll.surveyContract)
		} catch (error) {
			logger.error(error, 'Error editing survey')
			toast.error('An error occurred while editing the survey.')
		}
	})

	return (
		<ResponsiveDialog {...dialogProps}>
			<ResponsiveDialogContent>
				<div className="gap-8 overflow-y-auto flex flex-col">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>Edit Poll</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>

					<div className="max-h-[500px] overflow-y-auto relative">
						<PollForm form={form} variant={poll.status === 'ongoing' ? 'ongoing' : 'upcoming'} />
					</div>

					<ResponsiveDialogFooter>
						<Button
							onClick={onClickSubmit}
							disabled={!form.formState.isValid && !form.formState.isDirty}
							variant="danger"
							loading={form.formState.isSubmitting}
							className="py-5 px-10 leading-none"
							data-testid={testIdFactory.adminPollConfirmEditFormButton()}
						>
							Confirm
						</Button>
					</ResponsiveDialogFooter>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	)
}
